'use client';

import { useTransition } from 'react';
import { Briefcase, UserCheck, Mail, Copy, CheckCircle2, X } from '@cxnpl/ui/icons';
import { Main, H1, H3, IconButton, Stack, Text, XStack, YStack, Feature, Button } from '@cxnpl/ui';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'solito/navigation';
import { useAppSelector } from 'app/store';
import { selectAuthDetails } from 'app/features/landing/signup/signupSlice';
import i18n from 'app/i18n/i18n.config';
import { withTrackScreenView } from 'app/analytics';

const rowDetails = [
  {
    title: `${i18n.t('auth.signUpPage.successPageInfoOneTitle')}`,
    description: `${i18n.t('auth.signUpPage.successPageInfoOneDescription')}`,
    icon: <Briefcase />,
  },
  {
    title: `${i18n.t('auth.signUpPage.successPageInfoTwoTitle')}`,
    description: `${i18n.t('auth.signUpPage.successPageInfoTwoDescription')}`,
    icon: <UserCheck />,
  },
  {
    title: `${i18n.t('auth.signUpPage.successPageInfoThreeTitle')}`,
    description: `${i18n.t('auth.signUpPage.successPageInfoThreeDescription')}`,
    icon: <Mail />,
  },
];

export function SuccessStep() {
  const { t } = useTranslation();
  const router = useRouter();

  const { preferredName, customerNumber } = useAppSelector(selectAuthDetails);

  const copyToClipboard = async (text: string) => {
    await navigator.clipboard.writeText(text);
  };

  const [isPending, startTransition] = useTransition();

  return (
    <Main
      backgroundColor="$background/surface"
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height="100%"
      justifyContent="flex-start"
      alignItems="center"
      animation="250ms"
      enterStyle={{ y: '100%' }}
      exitStyle={{ y: '100%' }}
    >
      <IconButton
        icon={<X />}
        onPress={() => {
          router.push('/login');
        }}
        mode="secondary"
        variant="outlined"
        size="lg"
        testID="close-button"
        position="absolute"
        top={0}
        right="$space.5xl"
        zIndex={1}
      />

      <Stack alignItems="center" paddingHorizontal="$space.2xl" paddingBottom="$space.8xl" paddingTop="$space.2xl">
        <Stack width="100%" $tablet={{ width: 640 }}>
          <CheckCircle2 size="$size.5xl" color="$icon/success" />
          <H1 marginTop="$space.2xl" marginBottom="$space.lg">
            Thanks {preferredName}
          </H1>
          <Text variant="bodySmall">Your customer number to log in is </Text>
          <XStack alignItems="center">
            <H3>{customerNumber}</H3>
            <IconButton
              mode="primary"
              variant="icon-only"
              size="md"
              icon={<Copy />}
              onPress={() => {
                void copyToClipboard(customerNumber);
              }}
              hoverStyle={{ backgroundColor: '$background/primary-hover' }}
            />
          </XStack>
          <Text
            variant="bodySmall"
            marginBottom="$space.2xl"
          >{`We'll email this to you, please don't share it with anyone`}</Text>

          <H3 marginBottom="$space.2xl">{`What's next?`}</H3>

          <YStack gap="$space.lg" marginBottom="$space.4xl">
            {rowDetails.map((row) => {
              return (
                <Feature
                  key={`feature-${row.title}`}
                  title={t(row.title)}
                  icon={row.icon}
                  description={t(row.description)}
                />
              );
            })}
          </YStack>
          <Stack maxWidth="30%">
            <Button
              onPress={() => {
                startTransition(() => {
                  router.push('/login');
                });
              }}
              mode="primary"
              variant="filled"
              size="lg"
              style={{ textDecoration: 'underline' }}
              loading={isPending}
            >
              <Text variant="linkMedium">{t('auth.signUpPage.logInButton')}</Text>
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Main>
  );
}

export const SuccessStepWithAnalytics = withTrackScreenView(SuccessStep, {
  pageName: 'SignupSuccess',
});
